import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Input from '../components/input/Input';
import DatePicker from '../components/datepicker/DatePicker';
import DropDown from '../components/dropdown/DropDown';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { createProtector, getProtectorFormMetadata } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { cuitMask } from '../utils/Masks';

interface FormMetadata {
  document_types: Array<{ id: number; name: string }>;
  legal_entity_types: Array<{ id: number; name: string }>;
  meeting_minute_types: Array<{ id: number; name: string }>;
}

interface FormValues {
  company_name: string;
  cuit: string;
  email: string;
  registration_date: string;
  document_type_id: number | null;
  legal_entity_type_id: number | null;
  meeting_minute_type_id: number | null;
  minute_number: string;
}

const ProtectorNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const handleGoBack = () => {
    navigate(routes.fundsAndProtectors);
  };
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [minuteNumberError, setMinuteNumberError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<FormMetadata | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    company_name: '',
    cuit: '',
    email: '',
    registration_date: new Date().toISOString().split('T')[0],
    document_type_id: null,
    legal_entity_type_id: null,
    meeting_minute_type_id: null,
    minute_number: '',
  });

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getProtectorFormMetadata();
      if (response.success && 'data' in response) {
        setMetadata(response.data);
      } else {
        setError('Error al cargar los metadatos del formulario');
      }
    } catch (err) {
      setError('Error al cargar los metadatos del formulario');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateFormValues = (updates: Partial<FormValues>) => {
    setFormValues((prev) => ({ ...prev, ...updates }));
  };

  const handleCuitChange = (value: string) => {
    // Obtener el nombre del tipo de documento seleccionado
    const selectedDocType = metadata?.document_types?.find(
      (type) => type.id === formValues.document_type_id
    );
    
    // Si el tipo de documento es CUIT, aplicar la máscara
    if (selectedDocType?.name?.toUpperCase() === 'CUIT') {
      const maskedValue = cuitMask(value);
      updateFormValues({ cuit: maskedValue });
    } else {
      // Si no es CUIT, solo permitir números
      const numericValue = value.replace(/\D/g, '');
      updateFormValues({ cuit: numericValue });
    }
  };

  const isFormComplete = (): boolean => {
    return (
      formValues.company_name.trim() !== '' &&
      formValues.document_type_id !== null &&
      formValues.cuit.trim() !== '' &&
      formValues.email.trim() !== '' &&
      formValues.registration_date !== '' &&
      formValues.legal_entity_type_id !== null &&
      formValues.meeting_minute_type_id !== null &&
      formValues.minute_number.trim() !== '' &&
      !minuteNumberError
    );
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formValues.company_name.trim()) {
      errors.push('La razón social es obligatoria');
    }
    if (!formValues.document_type_id) {
      errors.push('El tipo de documento es obligatorio');
    }
    if (!formValues.cuit.trim()) {
      errors.push('El documento es obligatorio');
    }
    if (!formValues.email.trim()) {
      errors.push('El email es obligatorio');
    }
    if (!formValues.registration_date) {
      errors.push('La fecha es obligatoria');
    }
    if (!formValues.legal_entity_type_id) {
      errors.push('La entidad legal es obligatoria');
    }
    if (!formValues.meeting_minute_type_id) {
      errors.push('El tipo de acta es obligatorio');
    }
    if (!formValues.minute_number.trim()) {
      errors.push('El número de acta es obligatorio');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const MINUTE_NUMBER_ERROR_MSG = 'El número de acta ya está asignado a otro protector.';

  const isMinuteNumberDuplicateError = (message: string): boolean =>
    message.includes('número de acta ya está asignado');

  const handleSubmit = async () => {
    setError(null);
    setValidationErrors([]);
    setMinuteNumberError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const cleanedCuit = formValues.cuit ? formValues.cuit.replace(/-/g, '').replace(/\s/g, '') : null;
      
      const requestBody = {
        companyName: formValues.company_name,
        documentTypeId: formValues.document_type_id,
        legalEntityTypeId: formValues.legal_entity_type_id,
        cuit: cleanedCuit,
        email: formValues.email,
        meetingMinuteTypeId: formValues.meeting_minute_type_id,
        minuteNumber: formValues.minute_number,
        registrationDate: formValues.registration_date,
      };

      const response = await createProtector(requestBody);
      
      if (response.success) {
        navigate(routes.fundsAndProtectors);
      } else {
        const errorMessage = ('message' in response ? response.message : null) || 'Error al crear el protector';
        if (isMinuteNumberDuplicateError(errorMessage)) {
          setMinuteNumberError(MINUTE_NUMBER_ERROR_MSG);
        } else {
          setError(errorMessage);
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          'Error al crear el protector';
      if (isMinuteNumberDuplicateError(errorMessage)) {
        setMinuteNumberError(MINUTE_NUMBER_ERROR_MSG);
      } else {
        setError(errorMessage);
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Dropdown helpers for Document Type
  const documentTypeItems = metadata?.document_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedDocumentTypeName = () => {
    const selectedType = metadata?.document_types?.find(
      (type) => type.id === formValues.document_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleDocumentTypeChange = (selectedLabel: string) => {
    const type = metadata?.document_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ document_type_id: type.id });
      
      // Si el tipo de documento cambia a CUIT y ya hay un valor en el campo CUIT, aplicar la máscara
      if (type.name?.toUpperCase() === 'CUIT' && formValues.cuit.trim() !== '') {
        const maskedValue = cuitMask(formValues.cuit);
        updateFormValues({ cuit: maskedValue });
      }
    }
  };

  // Dropdown helpers for Legal Entity Type
  const legalEntityTypeItems = metadata?.legal_entity_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedLegalEntityTypeName = () => {
    const selectedType = metadata?.legal_entity_types?.find(
      (type) => type.id === formValues.legal_entity_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleLegalEntityTypeChange = (selectedLabel: string) => {
    const type = metadata?.legal_entity_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ legal_entity_type_id: type.id });
    }
  };

  // Dropdown helpers for Meeting Minute Type
  const meetingMinuteTypeItems = metadata?.meeting_minute_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedMeetingMinuteTypeName = () => {
    const selectedType = metadata?.meeting_minute_types?.find(
      (type) => type.id === formValues.meeting_minute_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleMeetingMinuteTypeChange = (selectedLabel: string) => {
    const type = metadata?.meeting_minute_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ meeting_minute_type_id: type.id });
    }
  };

  if (loading) {
    return (
      <Card width='-webkit-fill-available'>
        <Card display='flex' justifyContent='center' alignItems='center' height='60vh'>
          <Spinner />
        </Card>
      </Card>
    );
  }

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card margin={{ bottom: '3' }} display='flex' alignItems='center' gap='16px'>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Nuevo protector
        </H>
      </Card>

      {error && (
        <Card margin={{ bottom: '3' }}>
          <P variant='danger'>{error}</P>
        </Card>
      )}

      {validationErrors.length > 0 && (
        <Card margin={{ bottom: '3' }}>
          {validationErrors.map((err, index) => (
            <P key={index} variant='danger'>{err}</P>
          ))}
        </Card>
      )}

      <Card modifier='regular' spacing={'4'}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos del protector
          </H>
        </Card>

        {/* Fila 1 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'end',
          }}
        >
          <Input
            label="Razón social *"
            value={formValues.company_name}
            onChange={(e) => updateFormValues({ company_name: e.target.value })}
            size="md"
          />
          <DropDown
            id="document_type"
            label="Tipo de documento *"
            title={getSelectedDocumentTypeName()}
            items={documentTypeItems}
            onChange={handleDocumentTypeChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Documento *"
            value={formValues.cuit}
            onChange={(e) => handleCuitChange(e.target.value)}
            size="md"
          />
          <Input
            label="Email *"
            value={formValues.email}
            onChange={(e) => updateFormValues({ email: e.target.value })}
            size="md"
          />
        </div>

        {/* Fila 2 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            alignItems: 'end',
          }}
        >
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.registration_date}
                onChange={(date: string) => updateFormValues({ registration_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <DropDown
            id="legal_entity_type"
            label="Entidad legal *"
            title={getSelectedLegalEntityTypeName()}
            items={legalEntityTypeItems}
            onChange={handleLegalEntityTypeChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="meeting_minute_type"
            label="Tipo de acta *"
            title={getSelectedMeetingMinuteTypeName()}
            items={meetingMinuteTypeItems}
            onChange={handleMeetingMinuteTypeChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Número de acta *"
            value={formValues.minute_number}
            onChange={(e) => {
              updateFormValues({ minute_number: e.target.value });
              if (minuteNumberError) setMinuteNumberError(null);
            }}
            size="md"
            inputStatus={minuteNumberError ? 'error' : 'normal'}
            message={minuteNumberError || undefined}
          />
        </div>
      </Card>

      {/* Botón de confirmación */}
      <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={!isFormComplete() || submitting}
        >
          {submitting ? 'Creando...' : 'Confirmar creación'}
        </Button>
      </Card>
    </Card>
  );
};

export default ProtectorNew;
